<template>
  <form
    method="get"
    :action="submitUrl"
    class="hotel-search-bar"
    :class="[getClass]"
  >
    <H level="3" size="7">{{ t("headline") }}</H>
    <div class="hsb-location-select hsb-row">
      <InputLocation
        name="location"
        v-model="location"
        :placeholder="t('website.bookingbar.destination')"
        :empty-value="t('website.societym.panel_clickChooseCityHotel')"
        :all-in="t('website.general.location.xHotelsInLocation')"
        :continents="continents"
        fullwidth
        :disabled="disabledLocation"
      />
    </div>
    <div class="hsb-date-select hsb-row">
      <ClientOnly fallback-tag="div" fallback="loading...">
        <InputDate
          name="dates"
          :is-range="true"
          :start="start"
          :end="end"
          :placeholder="t('website.bookingbar.dates')"
          fullwidth
          @dateChange="setDate"
        />
      </ClientOnly>
    </div>
    <div class="hsb-people-select hsb-row">
      <InputPersonGuestCount
        name="count"
        :guests="guestsCount"
        :rooms="roomsCount"
        :placeholder="t('website.bookingbar.guestsAndRooms')"
        @countChange="setPeople"
        :button-text="t('website.societym.panel_select_people')"
        :more-than-ten-text="t('10+')"
      />
    </div>

    <Button
      type="submit"
      :disabled="disabledButton"
      class="hsb-submit"
      :fullwidth="!bar"
      tracking-area="search bar"
      >{{ submitLabel || t("website.button.search") }}</Button
    >
  </form>
</template>

<script setup lang="ts">
import type { Continent } from "~/shared/types/continents";

const { hotels } = useHotels();
const { t } = useI18n();

const props = defineProps({
  bar: {
    type: Boolean,
    default: false,
  },
  submitUrl: {
    type: String,
    default: "",
  },
  submitLabel: {
    type: String,
    default: null,
  },
  start: {
    type: Date,
    default: null,
  },
  end: {
    type: Date,
    default: null,
  },
  guests: {
    type: Number,
    default: 1,
  },
  rooms: {
    type: Number,
    default: 1,
  },
  infoMessagePeople: {
    type: String,
    default: "",
  },
  hasShadow: {
    type: Boolean,
    default: false,
  },
  disabledLocation: {
    type: Boolean,
    default: false,
  },
  disabledButton: {
    type: Boolean,
    default: false,
  },
  translations: {
    type: Object,
  },
});

const needsUpdate = ref(false);
const guestsCount = ref(props.guests);
const roomsCount = ref(props.rooms);
const tempDate = ref(props.start);
const location = defineModel<number | string>("location");

const setDate = (val) => {
  needsUpdate.value = true;
  tempDate.value = val;
};
const setPeople = () => {
  needsUpdate.value = true;
};

const getClass = computed(() => {
  return {
    "has-shadow": props.hasShadow,
    "show-as-bar": props.bar,
    "show-as-widget": !props.bar,
  };
});

const continents = computed(() =>
  hotels.value.reduce<Continent[]>((acc, item) => {
    let continent = acc.find((c) => c.cfId === item.continent.contentId);
    if (!continent && item.continent) {
      // Continent is not found, create a new one
      continent = {
        cfId: item.continent.contentId,
        id: item.continent.contentId,
        title: item.continent.title,
        type: "continent",
        cities: [],
      };
      acc.push(continent);
    }

    let city = continent?.cities?.find((c) => c.cfId === item.city.contentId);
    if (!city && item.city) {
      // City is not found, create a new one
      city = {
        cfId: item.city.contentId,
        id: item.city.contentId,
        code: item.city.code,
        title: item.city.title,
        type: "city",
        properties: [],
      };

      if (continent?.cities) {
        continent.cities.push(city);
      }
    }

    if (city?.properties) {
      city.properties.push({
        cfId: item.hotelContentfulId,
        id: item.propertyCode,
        title: item.name,
        type: "property",
        code: item.propertyCode,
      });
    }

    return acc;
  }, []),
);
</script>

<style lang="scss">
.hotel-search-bar {
  display: flex;
  flex-flow: column wrap;
  gap: var(--space-16);
  width: 100%;
  background: var(--primary-white);
  border-radius: var(--border-radius);

  &.has-shadow {
    margin-bottom: var(--space-24);
    padding: var(--space-16);
    box-shadow: var(--shadow);
  }

  .inp {
    font-family: var(--font-family-bold);
  }

  &.show-as-widget {
    .citizem-person-rooms-count-wrap {
      max-width: none;
    }
  }

  &.show-as-bar {
    > .headline {
      display: none;
    }
  }

  @include md {
    &.show-as-bar {
      flex-flow: row nowrap;

      .hsb-row {
        width: 29%;
      }

      .hsb-submit {
        width: 13%;
      }
    }

    &.has-shadow {
      padding: var(--space-24);
    }
  }
}

.hsb-row {
  width: 100%;

  .citizem-person-count-wrap {
    max-width: none;
  }
}
</style>
